import {
  useRestartTutorialMutation,
  useDeleteAccountMutation,
  useUnblockUserMutation,
  useBlockedQuery,
} from '@http';
import { GoogleSignIn } from '@components/auth/GoogleSignIn';
import { AppleSignIn } from '@components/auth/AppleSignIn';
import { Premium } from '@components/hub/player/Premium';
import { Avatars } from '@components/hub/player/Avatars';
import { useSettingsStore, useAppStore } from '@stores';
import { sendAppMessage } from '@helpers/mobileApp';
import { Checkbox } from '@components/ui/Checkbox';
import { Button } from '@components/ui/Button';
import { useTranslation } from 'react-i18next';
import { Modal } from '@components/ui/Modal';
import { useState } from 'react';

export function Settings({ close }: { close: () => void }) {
  const [showDelete, setShowDelete] = useState(false);

  const { t } = useTranslation(undefined, { keyPrefix: 'Settings' });
  const { data: blocked, refetch: refetchBlocked } = useBlockedQuery();
  const { mutateAsync: unblockAsync } = useUnblockUserMutation();
  const { mutateAsync: deleteAccountAsync } = useDeleteAccountMutation();
  const { mutateAsync: restartTutorialAsync } = useRestartTutorialMutation();
  const {
    hideIngameChat,
    setHideIngameChat,
    muted,
    setMuted
  } = useSettingsStore();
  const { device, refetch } = useAppStore();

  const logout = () => {
    localStorage.removeItem('authToken');
    window.location.reload();
  };

  const unblock = async (id: string) => {
    await unblockAsync({ id });
    refetch();
    refetchBlocked();
  };

  const restartTutorial = async () => {
    await restartTutorialAsync();
    refetch();
    close();
  };

  const openFeedback = () => {
    sendAppMessage('openLink', {
      url: 'https://battlechassis.featurebase.app/'
    });
  };

  const openPrivacy = () => {
    sendAppMessage('openLink', {
      url: 'https://battlechassis.com/privacy-policy.html'
    });
  };

  const openEula = () => {
    sendAppMessage('openLink', {
      url: 'https://www.apple.com/legal/internet-services/itunes/dev/stdeula/'
    });
  };

  const deleteAccount = async () => {
    await deleteAccountAsync();
    logout();
  };

  return (
    <>
      <Modal onRequestClose={() => setShowDelete(false)} title={t('deleteAccountModal')} isOpen={showDelete}>
        <p className="text-sm text-gray-300 p-2 bg-black pb-4">{t('deleteAccountText')}</p>

        <Button className="bg-red-600 border-red-600" onClick={deleteAccount}>
          {t('deleteAccountConfirm')}
        </Button>
      </Modal>

      <div className="p-2">
        {/* Premium */}
        <Premium />
        {/* Premium END */}

        {/* Avatars */}
        <Avatars />
        {/* Avatars END */}

        {/* Attribution */}
        <p className="text-sm text-gray-500 mt-6 mb-1">{t('attribution')}</p>

        <p className="text-sm text-gray-300">{t('att1')}</p>
        <p className="text-sm text-gray-300">{t('att2')}</p>
        {/* Attribution END */}

        {/* Blocked */}
        {(blocked?.length ?? 0) > 0 && (
          <>
            <p className="text-sm text-gray-500 mt-6 mb-1">{t('blocked')}</p>
            {blocked?.map((user) => (
              <div className="flex items-center justify-between" key={user.id}>
                <p className="text-sm text-gray-300">{user.username}</p>
                <button
                  className="bg-tertiary border border-secondary text-sm px-1"
                  onClick={() => unblock(user.id)}
                >
                  {t('unblock')}
                </button>
              </div>
            ))}
          </>
        )}
        {/* Blocked END */}

        {/* Game */}
        <p className="text-sm text-gray-500 mt-6 mb-1">{t('game')}</p>

        <Checkbox label={t('muteAudio')} onChange={setMuted} className="mb-2" checked={muted} />
        <Checkbox onChange={setHideIngameChat} label={t('hideIngameChat')} checked={hideIngameChat} className="mb-3" />

        <Button onClick={restartTutorial} className="mb-2">
          {t('restartTutorial')}
        </Button>
        <Button onClick={openFeedback}>
          {t('feedback')}
        </Button>
        {/* Game END */}

        {/* Account */}
        <p className="text-sm text-gray-500 mt-6 mb-2">{t('account')}</p>

        {device === 'ios' && <AppleSignIn />}
        {device !== 'ios' && <GoogleSignIn />}

        <Button className="mt-2 bg-red-600 border-red-600" onClick={logout}>
          {t('logout')}
        </Button>

        <Button className="mt-2 bg-red-600 border-red-600" onClick={() => setShowDelete(true)}>
          {t('deleteAccount')}
        </Button>
        {/* Account END */}

        {/* Legal */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-300 underline mt-4 cursor-pointer" onClick={openPrivacy}>
            {t('privacyPolicy')}
          </p>

          {device === 'ios' && (
            <p className="text-sm text-gray-300 underline mt-4 cursor-pointer" onClick={openEula}>
              EULA
            </p>
          )}
        </div>
        {/* Legal END */}
      </div>
    </>
  );
}